
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { BANK_TRANSFER_THRESHOLD } from "../../utils/constants";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDown, ChevronUp, FileText, CreditCard, Calendar, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface PaymentOptionsProps {
  onPayNow: () => void;
  onBankTransfer: () => void;
  amount: number;
}

const PaymentOptions = ({ onPayNow, onBankTransfer, amount }: PaymentOptionsProps) => {
  const { t, language } = useLanguage();
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const isPayLaterDisabled = amount < BANK_TRANSFER_THRESHOLD;

  const termsContent = language === 'ar' ? `
الشروط والأحكام

آخر تحديث: 9 فبراير 2025

مرحباً بكم في فيوجن، منصة رقمية تتيح للمستخدمين حجز ونشر الإعلانات التجارية على الشاشات الرقمية في المملكة العربية السعودية. يُرجى قراءة هذه الشروط والأحكام بعناية قبل استخدام المنصة. باستخدامك للمنصة، فإنك توافق على الالتزام بهذه الشروط والأحكام. إذا كنت لا توافق على هذه الشروط، يُرجى الامتناع عن استخدام المنصة.

1. التعريفات

المنصة: تشير إلى موقع فيوجن الإلكتروني وتطبيقاته الذكية والخدمات المتعلقة بحجز ونشر الإعلانات التجارية.

المستخدم: أي فرد أو كيان يستخدم المنصة، سواء كان معلناً أو وكالة إعلانية أو طرفاً آخر.

الإعلان: المحتوى المرئي أو الصوتي أو النصي المنشور على الشاشات الرقمية عبر المنصة.

الشاشات الرقمية: الشاشات الرقمية التي تعرض الإعلانات في مواقع مختلفة داخل المملكة العربية السعودية.

2. قبول الشروط

باستخدامك للمنصة، فإنك تؤكد أنك:

وصلت إلى سن الرشد القانوني (18 سنة) أو تستخدم المنصة تحت إشراف ولي أمرك.
توافق على الالتزام بجميع الشروط والأحكام المنصوص عليها هنا.
تتحمل المسؤولية الكاملة عن أي إعلانات تنشرها من خلال المنصة.

3. حجز ونشر الإعلانات

إنشاء حساب: يجب على المستخدمين إنشاء حساب على المنصة لحجز الإعلانات.
الموافقة على المحتوى: يجب أن تتوافق الإعلانات مع القوانين السعودية، بما في ذلك عدم احتوائها على محتوى مسيء أو محتوى ينتهك الشريعة الإسلامية أو اللوائح العامة.
الموافقة المسبقة: تحتفظ المنصة بالحق في رفض أو إزالة أي إعلان دون تقديم أسباب.
المدة والتكلفة: ستحدد مدة وتكلفة عرض الإعلان وفقاً للباقات المتاحة على المنصة.

4. الالتزامات القانونية

المحتوى المحظور: يُحظر نشر أي إعلانات تحتوي على:

مواد تنتهك الشريعة الإسلامية أو اللوائح السعودية.
محتوى مسيء أو عنصري أو يحرض على الكراهية.
معلومات كاذبة أو مضللة.
انتهاكات لحقوق الملكية الفكرية للآخرين.

المسؤولية القانونية: يتحمل المستخدم المسؤولية الكاملة عن أي انتهاكات قانونية ناتجة عن الإعلانات التي ينشرها.

5. المدفوعات والأمور المالية

طرق الدفع: توفر المنصة عدة طرق دفع إلكترونية وفقاً للأنظمة المعمول بها في المملكة.
الضرائب والرسوم: قد يتم تطبيق ضريبة القيمة المضافة أو رسوم أخرى وفقاً للوائح السعودية.
الإلغاء والاسترداد: تحدد سياسات الإلغاء والاسترداد وفقاً للشروط المنصوص عليها في المنصة.

6. حماية البيانات الشخصية

جمع البيانات: تجمع المنصة البيانات الشخصية للمستخدمين وفقاً لسياسة الخصوصية المعتمدة.
استخدام البيانات: تُستخدم البيانات لتحسين الخدمات وتوفير تجربة أفضل للمستخدم، مع الحفاظ على سرية المعلومات.
الحماية: تتخذ المنصة إجراءات أمنية لحماية بيانات المستخدمين من الوصول غير المصرح به.

7. المسؤولية

قيود المسؤولية: المنصة غير مسؤولة عن أي أضرار ناتجة عن الاستخدام الخاطئ للمنصة أو الأعطال التقنية خارجة عن سيطرتها.
التعويض: يوافق المستخدم على تعويض المنصة ضد أي أضرار أو خسائر ناتجة عن انتهاكه لهذه الشروط.

8. التعديلات

تحتفظ المنصة بالحق في تعديل هذه الشروط والأحكام في أي وقت. سيتم إشعار المستخدمين بأي تغييرات عبر البريد الإلكتروني أو من خلال الإعلانات على المنصة.

9. إنهاء الخدمة

تحتفظ المنصة بالحق في إنهاء أو تعليق حساب أي مستخدم في حالة انتهاك هذه الشروط أو القوانين المعمول بها.

10. القانون الحاكم

تخضع هذه الشروط والأحكام لقوانين المملكة العربية السعودية، وسيتم حل أي نزاعات من خلال المحاكم المختصة في المملكة.

11. اتصل بنا

لأي استفسارات أو شكاوى، يُرجى الاتصال بنا عبر:

البريد الإلكتروني: Support@fusion-media.sa
العنوان: الرياض

شكراً لاختياركم فيوجن.
  ` : `
Terms and Conditions

Last Updated: February 9, 2025

Welcome to Fusion, a digital platform that allows users to book and publish commercial advertisements on digital screens in the Kingdom of Saudi Arabia. Please read these terms and conditions carefully before using the platform. By using the platform, you agree to comply with these terms and conditions. If you do not agree with these terms, please refrain from using the platform.

1. Definitions

Platform: Refers to the Fusion website, its smart applications, and services related to booking and publishing commercial advertisements.

User: Any individual or entity using the platform, whether an advertiser, advertising agency, or other party.

Advertisement: The visual, audio, or textual content published on digital screens via the platform.

Digital Screens: Digital screens displaying advertisements at various locations within the Kingdom of Saudi Arabia.

2. Acceptance of Terms

By using the platform, you confirm that you:

• Have reached the legal age of majority (18 years) or are using the platform under the supervision of your guardian.
• Agree to comply with all the terms and conditions stated herein.
• Assume full responsibility for any advertisements you publish through the platform.

3. Booking and Publishing Advertisements

Account Creation: Users must create an account on the platform to book advertisements.
Content Approval: Advertisements must comply with Saudi laws, including not containing offensive content or content that violates Islamic law or public regulations.
Prior Approval: The platform reserves the right to reject or remove any advertisement without providing reasons.
Duration and Cost: The duration and cost of displaying the advertisement will be determined according to the available packages on the platform.

4. Legal Obligations

Prohibited Content: It is prohibited to publish any advertisements containing:

• Material that violates Islamic law or Saudi regulations.
• Offensive, racist, or hate-inciting content.
• False or misleading information.
• Violations of others' intellectual property rights.

Legal Responsibility: The user assumes full responsibility for any legal violations resulting from the advertisements they publish.

5. Payments and Financial Matters

Payment Methods: The platform offers several electronic payment methods in accordance with the applicable systems in the Kingdom.
Taxes and Fees: Value Added Tax (VAT) or other fees may be applied in accordance with Saudi regulations.
Cancellation and Refunds: Cancellation and refund policies are determined according to the terms stated on the platform.

6. Protection of Personal Data

Data Collection: The platform collects users' personal data in accordance with the approved privacy policy.
Data Usage: Data is used to improve services and provide a better user experience, while maintaining the confidentiality of information.
Protection: The platform takes security measures to protect users' data from unauthorized access.

7. Liability

Limitation of Liability: The platform is not liable for any damages resulting from incorrect use of the platform or technical failures beyond its control.
Indemnification: The user agrees to indemnify the platform against any damages or losses resulting from their violation of these terms.

8. Modifications

The platform reserves the right to modify these terms and conditions at any time. Users will be notified of any changes via email or through announcements on the platform.

9. Service Termination

The platform reserves the right to terminate or suspend any user's account in the event of a breach of these terms or applicable laws.

10. Governing Law

These terms and conditions are governed by the laws of the Kingdom of Saudi Arabia, and any disputes will be resolved through the competent courts in the Kingdom.

11. Contact Us

For any inquiries or complaints, please contact us via:

Email: Support@fusion-media.sa
Address: Riyadh

Thank you for choosing Fusion.
  `;

  return (
    <div className="space-y-6">
      {/* Terms and Conditions Section */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          <Button
            variant="ghost"
            onClick={() => setShowTerms(!showTerms)}
            className="w-full p-4 flex items-center justify-between hover:bg-muted/50 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <span className="font-medium">
                {language === 'ar' ? 'الشروط والأحكام' : 'Terms and Conditions'}
              </span>
            </div>
            {showTerms ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </Button>
          
          {showTerms && (
            <div className="border-t border-border/50">
              <ScrollArea className="h-64 px-4 py-3">
                <div className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                  {termsContent}
                </div>
              </ScrollArea>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Terms Agreement Checkbox */}
      <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border/50">
        <Checkbox
          id="terms-agreement"
          checked={termsAccepted}
          onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
          className="mt-0.5"
        />
        <label
          htmlFor="terms-agreement"
          className="text-sm cursor-pointer leading-relaxed"
        >
          {language === 'ar' ? (
            <>
              أوافق على{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="text-primary hover:underline font-medium"
              >
                الشروط والأحكام
              </button>
              {' '}والسياسات المعمول بها
            </>
          ) : (
            <>
              I agree to the{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="text-primary hover:underline font-medium"
              >
                Terms and Conditions
              </button>
              {' '}and applicable policies
            </>
          )}
        </label>
      </div>

      {/* Payment Options - Only enabled if terms are accepted */}
      <div className={`space-y-4 transition-all duration-300 ${!termsAccepted ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Pay Now Button */}
        <div className="relative group">
          <Button 
            className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] border-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            onClick={onPayNow}
            disabled={!termsAccepted}
          >
            <div className="flex items-center justify-center gap-2">
              <CreditCard className="w-5 h-5" />
              {language === 'ar' ? 'الدفع الآن' : 'Pay Now'}
            </div>
          </Button>
          {termsAccepted && (
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
          )}
        </div>

        {/* Pay Later Button */}
        <div className="relative group">
          <Button 
            className={`w-full h-14 text-lg font-semibold transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
              isPayLaterDisabled || !termsAccepted
                ? 'bg-muted text-muted-foreground cursor-not-allowed shadow-none' 
                : 'bg-gradient-to-r from-secondary to-secondary/90 hover:from-secondary/90 hover:to-secondary/80 text-secondary-foreground shadow-lg hover:shadow-xl border-0'
            }`}
            onClick={onBankTransfer}
            disabled={isPayLaterDisabled || !termsAccepted}
          >
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-5 h-5" />
              {t('payLater')}
            </div>
          </Button>
          {!isPayLaterDisabled && termsAccepted && (
            <div className="absolute inset-0 bg-gradient-to-r from-secondary/20 to-secondary/10 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
          )}
        </div>
      </div>

      {/* Agreement Required Notice */}
      {!termsAccepted && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 rounded-lg">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {language === 'ar' ? 'الموافقة مطلوبة' : 'Agreement Required'}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {language === 'ar' 
                ? 'يجب الموافقة على الشروط والأحكام للمتابعة'
                : 'Please accept the terms and conditions to proceed'}
            </p>
          </div>
        </div>
      )}
      
      {/* Minimum Amount Notice */}
      {isPayLaterDisabled && termsAccepted && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/30 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {language === 'ar' ? 'متطلبات الدفع المؤجل' : 'Pay Later Requirements'}
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              {language === 'ar' 
                ? 'يجب أن يكون المبلغ ٣٧٥,٠٠٠ ريال سعودي أو أكثر للدفع لاحقاً'
                : 'Amount must be SAR 375,000 or more to enable pay later option'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentOptions;
